/**
 * Double Ratchet com cabecalho cifrado (variante "header encryption" da
 * especificacao do Signal).
 *
 * Duas engrenagens:
 *  - **DH ratchet**: a cada virada de sentido, um par X25519 novo entra no
 *    root KDF. Isso da recuperacao pos-comprometimento — vazou a chave de
 *    hoje, amanha ja nao serve.
 *  - **Chain ratchet**: dentro de um sentido, cada mensagem avanca a chain
 *    key. Isso da sigilo futuro — vazou a chave de hoje, ontem continua ilegivel.
 *
 * O cabecalho tambem vai cifrado, com chave propria: sem isso o observador
 * leria os contadores e a chave publica do ratchet, e conseguiria ligar
 * mensagens a uma mesma conversa.
 */
import { open, seal } from './aead.js'
import { concat, readUint32BE, toBase64Url, writeUint32BE } from './bytes.js'
import {
  DecryptionFailedError,
  SessionNotReadyError,
  TooManySkippedMessagesError,
} from './errors.js'
import { kdfChainKey, kdfRootKey } from './kdf.js'
import { SIZES, sodium, type KeyPair } from './sodium.js'

/** Janela de tolerancia para mensagens fora de ordem. */
export const MAX_SKIPPED_MESSAGE_KEYS = 200

/** dhPublic(32) ‖ prevChainCount(4) ‖ messageIndex(4) */
export const HEADER_PLAINTEXT_BYTES = 40

/** nonce(24) ‖ cabecalho(40) ‖ tag(16) — tamanho fixo, por isso o quadro
 *  binario nao precisa carregar um campo de comprimento. */
export const ENCRYPTED_HEADER_BYTES = SIZES.aeadNonce + HEADER_PLAINTEXT_BYTES + SIZES.aeadTag

export interface RatchetHeader {
  readonly dhPublicKey: Uint8Array
  readonly prevChainCount: number
  readonly messageIndex: number
}

export interface RatchetMessage {
  readonly encryptedHeader: Uint8Array
  readonly ciphertext: Uint8Array
}

interface SkippedKey {
  readonly headerKey: Uint8Array
  readonly messageIndex: number
  readonly messageKey: Uint8Array
}

function encodeHeader(header: RatchetHeader): Uint8Array {
  const out = new Uint8Array(HEADER_PLAINTEXT_BYTES)
  out.set(header.dhPublicKey, 0)
  writeUint32BE(header.prevChainCount, out, 32)
  writeUint32BE(header.messageIndex, out, 36)
  return out
}

function decodeHeader(bytes: Uint8Array): RatchetHeader | undefined {
  if (bytes.length !== HEADER_PLAINTEXT_BYTES) return undefined
  return {
    dhPublicKey: bytes.slice(0, 32),
    prevChainCount: readUint32BE(bytes, 32),
    messageIndex: readUint32BE(bytes, 36),
  }
}

export interface SerializedSession {
  readonly v: 1
  readonly dhSelfPublic: string
  readonly dhSelfPrivate: string
  readonly dhRemote: string | null
  readonly rootKey: string
  readonly chainKeySend: string | null
  readonly chainKeyRecv: string | null
  readonly headerKeySend: string | null
  readonly nextHeaderKeySend: string
  readonly headerKeyRecv: string | null
  readonly nextHeaderKeyRecv: string
  readonly sendCount: number
  readonly recvCount: number
  readonly prevChainCount: number
  readonly skipped: readonly { hk: string; n: number; mk: string }[]
}

export class RatchetSession {
  private dhSelf: KeyPair
  private dhRemote: Uint8Array | undefined
  private rootKey: Uint8Array
  private chainKeySend: Uint8Array | undefined
  private chainKeyRecv: Uint8Array | undefined
  private headerKeySend: Uint8Array | undefined
  private nextHeaderKeySend: Uint8Array
  private headerKeyRecv: Uint8Array | undefined
  private nextHeaderKeyRecv: Uint8Array
  private sendCount = 0
  private recvCount = 0
  private prevChainCount = 0
  private skipped: SkippedKey[] = []

  private constructor(init: {
    dhSelf: KeyPair
    dhRemote: Uint8Array | undefined
    rootKey: Uint8Array
    chainKeySend: Uint8Array | undefined
    chainKeyRecv: Uint8Array | undefined
    headerKeySend: Uint8Array | undefined
    nextHeaderKeySend: Uint8Array
    headerKeyRecv: Uint8Array | undefined
    nextHeaderKeyRecv: Uint8Array
  }) {
    this.dhSelf = init.dhSelf
    this.dhRemote = init.dhRemote
    this.rootKey = init.rootKey
    this.chainKeySend = init.chainKeySend
    this.chainKeyRecv = init.chainKeyRecv
    this.headerKeySend = init.headerKeySend
    this.nextHeaderKeySend = init.nextHeaderKeySend
    this.headerKeyRecv = init.headerKeyRecv
    this.nextHeaderKeyRecv = init.nextHeaderKeyRecv
  }

  /**
   * Lado de quem inicia. Ja tem a chave publica de ratchet do outro (a
   * Signed PreKey dele), entao pode enviar antes de receber qualquer coisa.
   */
  static initiator(params: {
    sharedSecret: Uint8Array
    remoteRatchetPublicKey: Uint8Array
    initiatorHeaderKey: Uint8Array
    responderHeaderKey: Uint8Array
  }): RatchetSession {
    const dhSelf = sodium().crypto_box_keypair()
    const dh = sodium().crypto_scalarmult(dhSelf.privateKey, params.remoteRatchetPublicKey)
    const derived = kdfRootKey(params.sharedSecret, dh)
    return new RatchetSession({
      dhSelf,
      dhRemote: params.remoteRatchetPublicKey,
      rootKey: derived.rootKey,
      chainKeySend: derived.chainKey,
      chainKeyRecv: undefined,
      headerKeySend: params.initiatorHeaderKey,
      nextHeaderKeySend: derived.nextHeaderKey,
      headerKeyRecv: undefined,
      nextHeaderKeyRecv: params.responderHeaderKey,
    })
  }

  /** Lado de quem responde. So consegue enviar depois da primeira mensagem. */
  static responder(params: {
    sharedSecret: Uint8Array
    ownRatchetKeyPair: KeyPair
    initiatorHeaderKey: Uint8Array
    responderHeaderKey: Uint8Array
  }): RatchetSession {
    return new RatchetSession({
      dhSelf: params.ownRatchetKeyPair,
      dhRemote: undefined,
      rootKey: params.sharedSecret,
      chainKeySend: undefined,
      chainKeyRecv: undefined,
      headerKeySend: undefined,
      nextHeaderKeySend: params.responderHeaderKey,
      headerKeyRecv: undefined,
      nextHeaderKeyRecv: params.initiatorHeaderKey,
    })
  }

  get skippedCount(): number {
    return this.skipped.length
  }

  get sendCounter(): number {
    return this.sendCount
  }

  encrypt(plaintext: Uint8Array, associatedData: Uint8Array): RatchetMessage {
    if (this.chainKeySend === undefined || this.headerKeySend === undefined) {
      throw new SessionNotReadyError()
    }
    const step = kdfChainKey(this.chainKeySend)
    this.chainKeySend = step.chainKey

    const header = encodeHeader({
      dhPublicKey: this.dhSelf.publicKey,
      prevChainCount: this.prevChainCount,
      messageIndex: this.sendCount,
    })
    const encryptedHeader = seal(this.headerKeySend, header)
    this.sendCount += 1

    return {
      encryptedHeader,
      ciphertext: seal(step.messageKey, plaintext, concat(associatedData, encryptedHeader)),
    }
  }

  decrypt(message: RatchetMessage, associatedData: Uint8Array): Uint8Array {
    const fromSkipped = this.trySkipped(message, associatedData)
    if (fromSkipped !== undefined) return fromSkipped

    const decoded = this.decryptHeader(message.encryptedHeader)
    if (decoded === undefined) throw new DecryptionFailedError()

    if (decoded.isNewRatchet) {
      this.skipMessageKeys(decoded.header.prevChainCount)
      this.dhRatchet(decoded.header)
    }
    this.skipMessageKeys(decoded.header.messageIndex)

    if (this.chainKeyRecv === undefined) throw new DecryptionFailedError()
    const step = kdfChainKey(this.chainKeyRecv)
    this.chainKeyRecv = step.chainKey
    this.recvCount += 1

    const plaintext = open(
      step.messageKey,
      message.ciphertext,
      concat(associatedData, message.encryptedHeader),
    )
    if (plaintext === undefined) throw new DecryptionFailedError()
    return plaintext
  }

  /**
   * Tenta o cabecalho com a chave atual e depois com a proxima. Sucesso na
   * segunda significa que o outro lado virou o ratchet.
   */
  private decryptHeader(
    encryptedHeader: Uint8Array,
  ): { header: RatchetHeader; isNewRatchet: boolean } | undefined {
    if (this.headerKeyRecv !== undefined) {
      const plain = open(this.headerKeyRecv, encryptedHeader)
      const header = plain === undefined ? undefined : decodeHeader(plain)
      if (header !== undefined) return { header, isNewRatchet: false }
    }
    const plain = open(this.nextHeaderKeyRecv, encryptedHeader)
    const header = plain === undefined ? undefined : decodeHeader(plain)
    if (header !== undefined) return { header, isNewRatchet: true }
    return undefined
  }

  private trySkipped(message: RatchetMessage, associatedData: Uint8Array): Uint8Array | undefined {
    for (let i = 0; i < this.skipped.length; i++) {
      const entry = this.skipped[i]
      if (entry === undefined) continue
      const plain = open(entry.headerKey, message.encryptedHeader)
      if (plain === undefined) continue
      const header = decodeHeader(plain)
      if (header === undefined || header.messageIndex !== entry.messageIndex) continue

      const out = open(
        entry.messageKey,
        message.ciphertext,
        concat(associatedData, message.encryptedHeader),
      )
      if (out === undefined) throw new DecryptionFailedError()
      // Chave de mensagem e de uso unico: sai da lista assim que serve.
      this.skipped.splice(i, 1)
      return out
    }
    return undefined
  }

  private skipMessageKeys(until: number): void {
    if (this.chainKeyRecv === undefined || this.headerKeyRecv === undefined) return
    if (this.recvCount + MAX_SKIPPED_MESSAGE_KEYS < until) {
      // Um `until` absurdo seria um ataque de exaustao de memoria.
      throw new TooManySkippedMessagesError(MAX_SKIPPED_MESSAGE_KEYS)
    }
    while (this.recvCount < until) {
      const step = kdfChainKey(this.chainKeyRecv)
      this.chainKeyRecv = step.chainKey
      this.skipped.push({
        headerKey: this.headerKeyRecv,
        messageIndex: this.recvCount,
        messageKey: step.messageKey,
      })
      this.recvCount += 1
    }
    // Teto global: mesmo com varias viradas de ratchet, a lista nao cresce
    // sem limite. Descarta as mais antigas.
    if (this.skipped.length > MAX_SKIPPED_MESSAGE_KEYS) {
      this.skipped = this.skipped.slice(this.skipped.length - MAX_SKIPPED_MESSAGE_KEYS)
    }
  }

  private dhRatchet(header: RatchetHeader): void {
    this.prevChainCount = this.sendCount
    this.sendCount = 0
    this.recvCount = 0
    this.headerKeySend = this.nextHeaderKeySend
    this.headerKeyRecv = this.nextHeaderKeyRecv
    this.dhRemote = header.dhPublicKey

    const s = sodium()
    const recvSide = kdfRootKey(
      this.rootKey,
      s.crypto_scalarmult(this.dhSelf.privateKey, this.dhRemote),
    )
    this.rootKey = recvSide.rootKey
    this.chainKeyRecv = recvSide.chainKey
    this.nextHeaderKeyRecv = recvSide.nextHeaderKey

    this.dhSelf = s.crypto_box_keypair()
    const sendSide = kdfRootKey(
      this.rootKey,
      s.crypto_scalarmult(this.dhSelf.privateKey, this.dhRemote),
    )
    this.rootKey = sendSide.rootKey
    this.chainKeySend = sendSide.chainKey
    this.nextHeaderKeySend = sendSide.nextHeaderKey
  }

  /** Estado para persistir no banco cifrado. Contem material secreto. */
  serialize(): SerializedSession {
    const b64 = (b: Uint8Array | undefined): string | null =>
      b === undefined ? null : toBase64Url(b)
    return {
      v: 1,
      dhSelfPublic: toBase64Url(this.dhSelf.publicKey),
      dhSelfPrivate: toBase64Url(this.dhSelf.privateKey),
      dhRemote: b64(this.dhRemote),
      rootKey: toBase64Url(this.rootKey),
      chainKeySend: b64(this.chainKeySend),
      chainKeyRecv: b64(this.chainKeyRecv),
      headerKeySend: b64(this.headerKeySend),
      nextHeaderKeySend: toBase64Url(this.nextHeaderKeySend),
      headerKeyRecv: b64(this.headerKeyRecv),
      nextHeaderKeyRecv: toBase64Url(this.nextHeaderKeyRecv),
      sendCount: this.sendCount,
      recvCount: this.recvCount,
      prevChainCount: this.prevChainCount,
      skipped: this.skipped.map((k) => ({
        hk: toBase64Url(k.headerKey),
        n: k.messageIndex,
        mk: toBase64Url(k.messageKey),
      })),
    }
  }

  static deserialize(state: SerializedSession, decode: (s: string) => Uint8Array): RatchetSession {
    const opt = (s: string | null): Uint8Array | undefined => (s === null ? undefined : decode(s))
    const session = new RatchetSession({
      dhSelf: { publicKey: decode(state.dhSelfPublic), privateKey: decode(state.dhSelfPrivate) },
      dhRemote: opt(state.dhRemote),
      rootKey: decode(state.rootKey),
      chainKeySend: opt(state.chainKeySend),
      chainKeyRecv: opt(state.chainKeyRecv),
      headerKeySend: opt(state.headerKeySend),
      nextHeaderKeySend: decode(state.nextHeaderKeySend),
      headerKeyRecv: opt(state.headerKeyRecv),
      nextHeaderKeyRecv: decode(state.nextHeaderKeyRecv),
    })
    session.sendCount = state.sendCount
    session.recvCount = state.recvCount
    session.prevChainCount = state.prevChainCount
    session.skipped = state.skipped.map((k) => ({
      headerKey: decode(k.hk),
      messageIndex: k.n,
      messageKey: decode(k.mk),
    }))
    return session
  }
}
